import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  AwsPicture,
  ContactsRequest,
  Deposit,
  DepositContext as DepositContextType,
  User,
  VehicleDescription,
  VehicleDescriptionItem,
  VehicleType,
  VehicleVersion,
} from 'types';
import {
  ClassifiedsService,
  DepositPicturesService,
  Tracking,
  TRACKING_PAGES,
  UserManagementService,
  ValidImmatService,
} from 'api';
import { deleteCookie, getDepositTypeFromCookies, restoreDepositState } from 'utils';
import { ROUTES } from 'routes';
import { config } from 'config';
import { ValidateImmatResponse } from 'api/Dab/ValidImmatService';
import { ClassifiedManagementService } from 'api/Classifieds/ClassifiedManagementService';
import { Classified, NetworkWarranty, Vehicle } from '@groupelacentrale/classified-core';
import { Loader } from 'components';
import { formatDateReverse } from 'utils/date';
import * as Sentry from '@sentry/react';
import { allowDeposit } from 'utils/allowDeposit';

const initialDepositState = {
  needsManualTypeSelection: false,
  ngcData: false,
  ngcDown: false,
  vehicle: {},
  attempt: 0,
};

export const DepositContext = React.createContext<DepositContextType>({
  ...initialDepositState,
  validateImmatOrVin: () => Promise.resolve(),
  setDepositPictures: () => Promise.resolve(),
  setDepositType: () => undefined,
  setVehicleVersion: () => Promise.resolve(),
  setPictures: () => Promise.resolve(),
  setPrice: () => Promise.resolve(),
  setEquipmentsAndOptions: () => Promise.resolve(),
  setContacts: () => Promise.resolve(),
  publishClassified: () => Promise.reject(),
  resetDeposit: () => undefined,
});

export const DepositProvider: React.FC<{ user: User }> = ({ children, user }) => {
  const [loading, setLoading] = useState(true);
  const [deposit, setDeposit] = useState<Deposit>(initialDepositState);
  const history = useHistory();
  const isPro = user.isPro;

  const setDepositClassified = useCallback(async () => {
    const classifiedReference = localStorage.getItem('classifiedReference');

    if (classifiedReference) {
      const classified = await ClassifiedManagementService.getClassified(classifiedReference);
      Sentry.configureScope(scope => {
        scope.setTag('reference', classified.reference);
      });
      setDeposit(restoreDepositState(classified));
      setLoading(false);

      if (classified.status === 'PARTIAL') return;
    }

    history.replace(ROUTES.registration);
    setLoading(false);
  }, [history]);

  const setDepositPictures = useCallback(async () => {
    const classifiedReference = localStorage.getItem('classifiedReference');
    const classifiedPictures = localStorage.getItem(`${classifiedReference}-awsPictures`);

    if (classifiedReference && classifiedPictures) {
      const awsPictures = JSON.parse(classifiedPictures);
      await DepositPicturesService.loadAWSPictures(awsPictures).then(pictures =>
        setDeposit(prevState => ({ ...prevState, pictures })),
      );
    }
    setDepositClassified();
  }, [setDepositClassified]);

  const storePartialDepositPictures = () => {
    const picturesKeys = Object.keys(localStorage).filter(k => k.includes('-awsPictures'));
    const pictures = picturesKeys.map(key => localStorage.getItem(key));
    localStorage.clear();
    pictures.forEach((p, i) => localStorage.setItem(`${picturesKeys[i].slice(0, 10)}-awsPictures`, p || ''));
    setLoading(false);
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_CI === 'true') {
      // @ts-ignore Cypress access to the local state, removed when built
      window.Deposit = { setDeposit, history };
    }
  }, [history]);

  useEffect(() => {
    switch (history.location.pathname) {
      case ROUTES.vehicleVersionChoice:
      case ROUTES.vehicleDescription:
      case ROUTES.vehiclePrice:
      case ROUTES.contactInformation:
      case ROUTES.phoneValidation:
      case ROUTES.publicationOptions:
        setDepositClassified();
        break;
      case ROUTES.photos:
        setDepositPictures();
        break;
      case ROUTES.registration:
        storePartialDepositPictures();
        break;
      default:
        setLoading(false);
        break;
    }
  }, [history, setDepositClassified, setDepositPictures]);

  useEffect(() => {
    if (deposit.needsManualTypeSelection) {
      setDeposit(prevState => ({ ...prevState, needsManualTypeSelection: false }));
      history.push(ROUTES.vehicleType);
    }
  }, [deposit.needsManualTypeSelection, history]);

  useEffect(() => {
    const registration = deposit.vehicle.registration;
    const vin = deposit.vehicle.vin?.slice(-3);
    const partCanContinue = !isPro && registration && vin && deposit.depositType !== 'UNKNOWN';
    const proCanContinue = isPro && (registration || vin) && deposit.depositType !== 'UNKNOWN';

    if (!deposit.attempt) {
      return;
    }

    if (partCanContinue || proCanContinue) {
      deleteCookie('origin');
      if (process.env.REACT_APP_CI === 'true' || process.env.NODE_ENV === 'development') {
        localStorage.setItem('immatriculation', registration || '');
        localStorage.setItem('vin', vin || '');
        localStorage.setItem('depositType', deposit.depositType!);
        // Stop cypress tests after Registration page for these categories
        if (!allowDeposit(deposit.depositType!)) {
          return;
        }
      }
      if (!allowDeposit(deposit.depositType!)) {
        if (isPro && (deposit.ngcDown || !deposit.ngcData)) {
          window.location.href =
            config.URL_LEGACY_DEPOSIT +
            `deposit/identification.html?immat=&vin=&vertical=${deposit.depositType!.toLowerCase()}`;
        } else {
          window.location.href =
            config.URL_LEGACY_DEPOSIT +
            `deposit/identification.html?immat=${registration}&vin=${vin}&vertical=${deposit.depositType!.toLowerCase()}`;
        }
      } else {
        new Promise<Classified>(async resolve => {
          const emailingMetadata = user.isPro ? undefined : await UserManagementService.getUserEmailingData(user.id);
          const classified = await ClassifiedManagementService.createClassified(
            {
              ngcData: deposit.ngcData,
              registerNumber: deposit.vehicle.registration!,
              vin: deposit.vehicle.vin!,
              registerType: deposit.vehicle.registrationType!,
              target: emailingMetadata,
            },
            deposit.depositType!,
          );
          resolve(classified);
        })
          .then((classified: Classified) => {
            Sentry.configureScope(scope => {
              scope.setTag('reference', classified.reference);
            });
            setDeposit(restoreDepositState(classified));
            history.push(ROUTES.vehicleVersionChoice);
          })
          .catch(r => {
            history.push({ pathname: ROUTES.registration, search: `error=${r.message}` });
          });
      }
    }
  }, [
    deposit.vehicle.registration,
    deposit.vehicle.vin,
    deposit.depositType,
    deposit.ngcData,
    deposit.ngcDown,
    deposit.vehicle.registrationType,
    deposit.reference,
    deposit.attempt,
    isPro,
    history,
    user,
  ]);

  const validateImmatOrVin = useCallback(
    (registration?: string, vin?: string) => {
      return ValidImmatService.validate({ registration, vin, isPro }).then(async (data: ValidateImmatResponse) => {
        const depositType = getDepositTypeFromCookies(data.vehicleType);
        const ngcData = data.ngcData;
        const partAndInvalidVin = ngcData && !isPro && vin !== data.vin.slice(-3);
        const proAndInvalidVin = ngcData && isPro && vin && vin !== data.vin;
        const partAndUnknownRegistrationType = !ngcData && !isPro && data.immatOrigin === 'UNKNOWN';
        const page = TRACKING_PAGES.IMMAT_PART();
        const context = {
          '41': '',
          '38': isPro ? '[pro_etape_0_immat_erreur]' : '[etape_0_immat_erreur]',
        };

        // Lorsque l'utilisateur vient de LC ou Pilot et qu'on a pas de données de NGC
        const partAndSivAndInvalidVehicleType =
          !ngcData && !isPro && data.immatOrigin === 'SIV' && ['AUTO', 'MOTO', 'UTILITAIRE'].includes(depositType);

        let availableRegistration = true;
        let availableVin = true;
        if (!isPro) {
          availableRegistration = await ClassifiedsService.isRegistrationAvailable(registration || data.immat).then(
            r => r.available,
          );
        } else {
          if (registration) {
            availableRegistration = await ClassifiedsService.isRegistrationAvailable(registration || data.immat).then(
              r => r.available,
            );
          } else {
            availableVin = await ClassifiedsService.isVinAvailable(vin).then(r => r.available);
          }
        }

        if (
          partAndInvalidVin ||
          partAndUnknownRegistrationType ||
          partAndSivAndInvalidVehicleType ||
          proAndInvalidVin ||
          !availableRegistration ||
          !availableVin
        ) {
          if (partAndInvalidVin || proAndInvalidVin) {
            context['41'] = '[erreurVin]';
          } else {
            context['41'] = '[erreurImmat]';
          }
          Tracking.setPageError();
          Tracking.setContext(context);
          Tracking.sendPage(page);

          if (!availableRegistration) {
            throw new Error("L'immatriculation est déjà utilisée");
          }
          if (!availableVin) {
            throw new Error('Le VIN est déjà utilisé');
          }

          if (partAndInvalidVin) {
            throw new Error('error_vin');
          }

          throw new Error('Dépôt impossible avec cette immatriculation');
        }

        setDeposit(prevState => ({
          ...prevState,
          attempt: prevState.attempt + 1,
          depositType,
          ngcData,
          ngcDown: data.ngcDown,
          needsManualTypeSelection: depositType === 'UNKNOWN',
          vehicle: {
            registration: data.immat || registration,
            vin: data.vin,
            registrationType: data.immatOrigin,
          },
        }));
      });
    },
    [isPro],
  );

  const setDepositType = useCallback(
    (vehicleType: VehicleType) => {
      if (
        !deposit.ngcData &&
        !isPro &&
        ['AUTO', 'MOTO', 'UTILITAIRE'].includes(vehicleType) &&
        deposit.vehicle.registrationType === 'SIV'
      ) {
        Tracking.setContext({
          '41': '[erreurImmat]',
          '38': isPro ? '[pro_etape_0_immat_erreur]' : '[etape_0_immat_erreur]',
        });
        Tracking.setPageError();
        setDeposit(() => {
          history.replace(ROUTES.registration);
          throw new Error('Dépôt impossible avec cette immatriculation');
        });
      }
      setDeposit(prevState => ({ ...prevState, needsManualTypeSelection: false, depositType: vehicleType }));
    },
    [deposit.vehicle.registrationType, deposit.ngcData, isPro, history],
  );

  const setVehicleVersion = useCallback(
    (versionId: string, vehicleVersion: VehicleVersion, referencePro?: string) => {
      // Send only main fields
      const {
        firstCirculationDate,
        make,
        model,
        category,
        version,
        imported,
        firstHand,
        mileage,
        condition,
        internalColor,
        externalColor,
        seatMaterial,
        paintType,
      } = vehicleVersion;

      const vehicle = {
        make,
        model,
        category,
        version,
        versionId,
        imported,
        firstHand,
        mileage,
        condition,
        ...(!['MOTO', 'CAMPING-CAR', 'CARAVANE'].includes(deposit.depositType!) && {
          internalColor: `${seatMaterial} ${internalColor}`,
        }),
        externalColor: `${externalColor} ${paintType}`,
        firstTrafficDate: formatDateReverse(firstCirculationDate!),
      };

      // To remove after equipments API update
      category && localStorage.setItem('category', category);

      return ClassifiedManagementService.updateClassified({
        vehicle,
        referencePro,
      }).then((classified: Classified) => {
        setDeposit(restoreDepositState(classified));
        history.push(ROUTES.vehicleDescription);
      });
    },
    [history, deposit.depositType],
  );

  const setPictures = useCallback(async (pictures: File[]) => {
    return DepositPicturesService.upload(pictures).then(() => setDeposit(prevState => ({ ...prevState, pictures })));
  }, []);

  const setContacts = useCallback(
    (contacts: ContactsRequest, firstname?: string, lastname?: string, zipCode?: string | null) => {
      return ClassifiedManagementService.updateClassified({
        contacts,
        firstname,
        lastname,
        // @ts-ignore
        zipCode,
      }).then((classified: Classified) => setDeposit(restoreDepositState(classified)));
    },
    [],
  );

  const setPrice = useCallback((price: number) => {
    return ClassifiedManagementService.updateClassified({ price }).then((classified: Classified) =>
      setDeposit(restoreDepositState(classified)),
    );
  }, []);

  const setEquipmentsAndOptions = useCallback(
    async ({ equipments, options, packs, repairs, duration, designation }: VehicleDescription) => {
      const vehicle: Vehicle = {
        options: options.map((option: VehicleDescriptionItem) => ({
          name: option.label,
        })),
        equipments,
        packs: packs.map((pack: VehicleDescriptionItem) => ({
          details: [{ name: pack.label }],
        })),
        repairs: repairs.map(r => r.label),
      };

      const networkWarranty: NetworkWarranty | undefined =
        duration || (designation && Object.keys(designation).length > 0) ? { duration, designation } : undefined;

      const classified = await ClassifiedManagementService.updateClassified({ vehicle, networkWarranty });
      setDeposit(restoreDepositState(classified));
    },
    [],
  );

  const uploadPhotos = useCallback(async (classified: Classified, media: string, action: string, type: string) => {
    const classifiedPictures = localStorage.getItem(`${classified.reference}-awsPictures`);
    if (classifiedPictures) {
      const awsPictures: AwsPicture[] = JSON.parse(classifiedPictures).map((picture: AwsPicture) => ({
        action,
        type,
        bucket: picture.bucket,
        key: picture.key,
      }));
      await ClassifiedManagementService.uploadPhoto(classified, awsPictures, media).catch(() =>
        alert("Erreur lors de l'upload des photos"),
      );
    }
  }, []);

  const publishClassified = useCallback(
    async (publicationOptions?: string[]): Promise<Classified> => {
      const registration = deposit.vehicle.registration;
      const vin = deposit.vehicle.vin;
      let availableRegistration = true;
      let availableVin = true;
      if (!isPro) {
        availableRegistration = await ClassifiedsService.isRegistrationAvailable(registration).then(r => r.available);
      } else {
        if (registration) {
          availableRegistration = await ClassifiedsService.isRegistrationAvailable(registration).then(r => r.available);
        } else {
          availableVin = await ClassifiedsService.isVinAvailable(vin).then(r => r.available);
        }
      }
      if (!availableRegistration || !availableVin) {
        throw new Error("L'immatriculation est déjà utilisée");
      }
      const classified = await ClassifiedManagementService.publishClassified(publicationOptions);
      setDeposit(restoreDepositState(classified));
      await uploadPhotos(classified, 'VO', 'INSERT', 'STANDARD');
      return classified;
    },
    [uploadPhotos, deposit.vehicle, isPro],
  );

  const resetDeposit = useCallback(() => {
    setDeposit(initialDepositState);
  }, []);

  const contextValue = useMemo(
    (): DepositContextType => ({
      ...deposit,
      validateImmatOrVin,
      setDepositPictures,
      setDepositType,
      setVehicleVersion,
      setPictures,
      setPrice,
      setEquipmentsAndOptions,
      setContacts,
      publishClassified,
      resetDeposit,
    }),
    [
      deposit,
      validateImmatOrVin,
      setDepositPictures,
      setDepositType,
      setVehicleVersion,
      setPictures,
      setPrice,
      setEquipmentsAndOptions,
      setContacts,
      publishClassified,
      resetDeposit,
    ],
  );

  return <DepositContext.Provider value={contextValue}>{loading ? <Loader /> : children}</DepositContext.Provider>;
};

export const useDeposit = (): DepositContextType => useContext(DepositContext);
